
import React, { useState, useEffect } from 'react';
import { useLocalization } from '../hooks/useLocalization';

interface CookiePreferences {
    necessary: boolean;
    analytics: boolean;
    marketing: boolean;
    preferences: boolean;
}

const defaultPreferences: CookiePreferences = {
    necessary: true, // Always true and disabled
    analytics: false,
    marketing: false,
    preferences: false,
};

const STORAGE_KEY = 'tourvisio_cookie_consent_v2'; // Changed key to force display for user

export const CookieConsent: React.FC = () => {
    const { language } = useLocalization();
    const [isBannerVisible, setIsBannerVisible] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [prefs, setPrefs] = useState<CookiePreferences>(defaultPreferences);

    useEffect(() => {
        const storedPrefs = localStorage.getItem(STORAGE_KEY);
        if (storedPrefs) {
            const parsed = JSON.parse(storedPrefs);
            setPrefs(parsed);
            applyCookieSettings(parsed);
        } else {
            // Slight delay to ensure animation plays nicely on load
            setTimeout(() => setIsBannerVisible(true), 500);
        }
    }, []);

    const applyCookieSettings = (settings: CookiePreferences) => {
        // Logic to enable/disable scripts based on consent
        if (settings.analytics) {
            console.log("✅ Analytics Enabled");
            // window.gtag(...)
        }
        if (settings.marketing) {
            console.log("✅ Marketing Enabled");
            // fbq(...)
        }
    };

    const handleSave = async (newPrefs: CookiePreferences) => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newPrefs));
        setPrefs(newPrefs);
        applyCookieSettings(newPrefs);
        setIsBannerVisible(false);
        setIsModalOpen(false);

        // Send to backend
        try {
            const action = Object.values(newPrefs).every(v => v) ? 'accept' :
                Object.values(newPrefs).every(v => !v) ? 'reject' : 'partially_accepted';

            await fetch('/api/cookies/consent', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: action,
                    categories: newPrefs,
                    deviceType: window.innerWidth < 768 ? 'mobile' : 'desktop'
                })
            });
        } catch (error) {
            console.error('Failed to sync cookie consent:', error);
        }
    };

    const handleAcceptAll = () => {
        const allTrue = { necessary: true, analytics: true, marketing: true, preferences: true };
        handleSave(allTrue);
    };

    const handleRejectAll = () => {
        const allFalse = { necessary: true, analytics: false, marketing: false, preferences: false };
        handleSave(allFalse);
    };

    const togglePref = (key: keyof CookiePreferences) => {
        if (key === 'necessary') return;
        setPrefs(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const t = {
        bannerText: language === 'sv'
            ? "Vi använder cookies för att förbättra din upplevelse."
            : "We use cookies to improve your experience.",
        accept: language === 'sv' ? "Godkänn" : "Accept",
        reject: language === 'sv' ? "Neka" : "Reject",
        settings: language === 'sv' ? "Inställningar" : "Settings",
        save: language === 'sv' ? "Spara val" : "Save Preferences",
        title: language === 'sv' ? "Cookie-inställningar" : "Cookie Preferences",
        desc: language === 'sv' ? "Välj vilka cookies du vill tillåta." : "Choose which cookies you want to allow.",
        catNec: language === 'sv' ? "Nödvändiga" : "Necessary",
        catAna: language === 'sv' ? "Analys" : "Analytics",
        catMkt: language === 'sv' ? "Marknadsföring" : "Marketing",
        catPrf: language === 'sv' ? "Funktionella" : "Preferences"
    };

    if (!isBannerVisible && !isModalOpen) return null;

    return (
        <>
            {/* Ultra-Thin Banner */}
            {isBannerVisible && !isModalOpen && (
                <div className="fixed bottom-0 left-0 w-full bg-gray-900/95 backdrop-blur-md text-white z-[100] border-t border-gray-800 shadow-2xl animate-slide-up">
                    <div className="container mx-auto px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
                        <div className="flex items-center gap-3 text-xs font-medium text-gray-300">
                            <span className="text-lg">🍪</span>
                            <p>{t.bannerText}</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setIsModalOpen(true)}
                                className="text-[10px] font-bold uppercase tracking-wider text-gray-400 hover:text-white transition-colors px-2"
                            >
                                {t.settings}
                            </button>
                            <div className="h-3 w-px bg-gray-700 mx-1"></div>
                            <button
                                onClick={handleRejectAll}
                                className="text-[10px] font-bold uppercase tracking-wider text-gray-400 hover:text-white transition-colors px-2"
                            >
                                {t.reject}
                            </button>
                            <button
                                onClick={handleAcceptAll}
                                className="bg-white text-gray-900 hover:bg-gray-200 text-[10px] font-bold uppercase tracking-wider px-4 py-1.5 rounded-full transition-colors shadow-lg"
                            >
                                {t.accept}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden animate-fade-in-up">
                        <div className="p-5 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
                            <h3 className="font-bold text-gray-900">{t.title}</h3>
                            <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-black">✕</button>
                        </div>
                        <div className="p-5 space-y-4">
                            <p className="text-xs text-gray-500 mb-4">{t.desc}</p>

                            {[
                                { id: 'necessary', label: t.catNec, disabled: true },
                                { id: 'analytics', label: t.catAna, disabled: false },
                                { id: 'marketing', label: t.catMkt, disabled: false },
                                { id: 'preferences', label: t.catPrf, disabled: false },
                            ].map((item) => (
                                <div key={item.id} className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-700">{item.label}</span>
                                    <label className={`relative inline-flex items-center cursor-pointer ${item.disabled ? 'opacity-50' : ''}`}>
                                        <input
                                            type="checkbox"
                                            className="sr-only peer"
                                            checked={prefs[item.id as keyof CookiePreferences]}
                                            disabled={item.disabled}
                                            onChange={() => togglePref(item.id as keyof CookiePreferences)}
                                        />
                                        <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                                    </label>
                                </div>
                            ))}
                        </div>
                        <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-2">
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="px-4 py-2 text-xs font-bold text-gray-600 hover:text-black"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleSave(prefs)}
                                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg shadow-md transition-colors"
                            >
                                {t.save}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
